import { Component, Input, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSort, Sort, SortDirection } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { UserBase } from '@core/core.state';
import { CustomerSpaceUserModel } from '@core/customer-space-admin/customer-space-user.model';
import { compare, concatCustomerUserFields } from '@core/customer-space-admin/utils/cs-user.utils';
import { TranslateService } from '@ngx-translate/core';
import { OptionsStoreService, TableOptionsState, UiTableKeys } from '@platform/components/exper-table/sort-options-store.service';
import { defaultPageSize } from '@platform/shared/utilities/paginator-utility';
import { UserType } from '@platform/components/user-is-external-dropdown/user-is-external-dropdown.component';

@Component({
  template: '',
})
export class UsersTableBaseComponent<T extends UserBase> implements OnInit {
  @Input() users: T[] = [];
  sUsers: CustomerSpaceUserModel[] = [];

  selectedUserType: UserType = null;
  searchTerm: string;

  customerSpaceDataSource: MatTableDataSource<CustomerSpaceUserModel> = new MatTableDataSource<CustomerSpaceUserModel>();
  sortOptions: Sort; // options from local storage
  tableOptions: TableOptionsState;

  constructor(
    private _optionsStoreService: OptionsStoreService,
    private translateService: TranslateService,
  ) {
    // set from local storage
    this.tableOptions = this._optionsStoreService.getOptionsOrDefault(this.uiTabSortingStorageKey);
    this.sortOptions = this.tableOptions.sort;
    this.selectedUserType = this.tableOptions.userTypeFilter as UserType;

    this.customerSpaceDataSource = new MatTableDataSource<CustomerSpaceUserModel>([]);
    this.customerSpaceDataSource.sortData = this.sortData2;
    this.customerSpaceDataSource.filterPredicate = this.filterPredicate();
    this.applyFilter();
  }

  get uiTabSortingStorageKey(): UiTableKeys {
    throw new Error('UiTableKeys not defined');
  }

  // sort options from localStorage
  get initialSortActive(): string {
    return this.sortOptions.active;
  }

  get initialSortDirection(): SortDirection {
    return this.sortOptions.direction;
  }

  get initialPage(): number {
    return this.tableOptions.pageIndex || 0;
  }

  get initialPageSize(): number {
    return this.tableOptions.pageSize || defaultPageSize;
  }

  ngOnInit(): void {
    this.sUsers = this.mapUsers(this.users || []);

    //this.customerSpaceDataSource = new MatTableDataSource<CustomerSpaceUser>(this.sUsers);
    this.customerSpaceDataSource.data = this.sUsers;
  }

  getTranslatedRoleName(user: T): string {
    throw new Error('getTranslatedRoleName not implemented');
    /*
    const roleName = this.translate(`roles.${user.customerSpaceRole.name.toLocaleLowerCase()}`);
    const deputy = user.isDeputy ? ` (${this.translate('users.user-profile.deputy')})` : '';

    return roleName + deputy;
    */
  }

  getTranslatedRoleName2(role: string): string {
    const roleName = this.translate(`roles.${role.toLocaleLowerCase()}`);

    return roleName;
  }

  getTranslatedDeputy(): string {
    return `${this.translate('users.user-profile.deputy-from')}`;
  }

  mapUser(user: T): CustomerSpaceUserModel {
    throw new Error('mapUser not implmented');
    /*
    const res = {
      id: user.id,
      userName: {
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.customerSpaceRole?.name,
        roleText: user.customerSpaceRole ? this.getTranslatedRoleName2(user.customerSpaceRole.name) : '',
        isContact: user.isContact,
        isDeputy: user.isDeputy,
        contactText: user.isContact ? this.translate('customer-spaces.user-management.contact') : '',
        deputyText: user.isDeputy ? this.getTranslatedDeputy() : '',
      },
      account: {
        isActive: user.isActive,
        isInternal: user.isInternal,
      },
      applications: [],
      organizationUnit: user.organizationUnit,
      _original: user,
    } as CustomerSpaceUserModel;
    return res;
    */
  }

  mapUsers(users: T[]): CustomerSpaceUserModel[] {
    return users.map((u) => this.mapUser(u));
  }

  translate(key: string) {
    return this.translateService.instant(key);
  }

  /** Announce the change in sort state for localstorage */
  storeSortOptions(sortState: Sort) {
    this._optionsStoreService.patchOptions(this.uiTabSortingStorageKey, { sort: sortState });
    this.sortOptions = sortState;
  }

  storePageOptions(event: PageEvent) {
    this._optionsStoreService.patchOptions(this.uiTabSortingStorageKey, { pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  onUserTypeChange(event: UserType) {
    this._optionsStoreService.patchOptions(this.uiTabSortingStorageKey, { userTypeFilter: event });
    this.selectedUserType = event;
    this.applyFilter();
  }

  // This is the method which get called from your filter input
  onSearchChange(filterValue: string) {
    filterValue = filterValue || '';
    this.searchTerm = filterValue.trim().toLowerCase();
    this.applyFilter();
  }

  applyFilter() {
    const params = new URLSearchParams();
    params.set('s', this.searchTerm || '');
    params.set('ut', this.selectedUserType || '');
    this.customerSpaceDataSource.filter = params.toString();
  }

  // custom filter function
  filterPredicate() {
    const filterFunction = (data: CustomerSpaceUserModel, filter: string): boolean => {
      const queryParams = new URLSearchParams(filter);
      const searchTerm = queryParams.get('s') || '';
      const userType = queryParams.get('ut');

      const isInternal = data.account.isInternal;

      if (userType === 'internal' && !isInternal) {
        return false;
      }

      if (userType === 'external' && isInternal) {
        return false;
      }

      if (searchTerm === 'active' || searchTerm === 'activ') {
        return data.account.isActive;
      }
      if (searchTerm === 'inactive' || searchTerm === 'inactiv') {
        return !data.account.isActive;
      }
      const concatFields = concatCustomerUserFields(data, this.translateService);
      return concatFields.indexOf(searchTerm) !== -1;
    };

    return filterFunction;
  }

  sortData2(items: CustomerSpaceUserModel[], sort: MatSort): CustomerSpaceUserModel[] {
    if (!sort.active || sort.direction === '') {
      return items;
    }

    const isAsc = sort?.direction === 'asc';

    return items.sort((a: CustomerSpaceUserModel, b: CustomerSpaceUserModel) => {
      switch (sort?.active) {
        case 'name':
          return compare(a.userName.firstName + a.userName.lastName, b.userName.firstName + a.userName.lastName, isAsc);
        case 'account':
          return compare(+a.account.isActive, +b.account.isActive, isAsc);
        case 'organization':
          return compare(a.organizationUnit, b.organizationUnit, isAsc);
        case 'applications':
          return compare(a.applications.length, b.applications.length, isAsc);
        default:
          return 0;
      }
    });
  }

  toCustomerSpaceUser(row: CustomerSpaceUserModel): T {
    return row._original as T;
  }

  public onAftereRefreshData(): void {
    // console.log('BASE onAftereRefreshData was called');
  }

  // TODO: find better solution with Observable<any[]>
  public refereshData(newData: any[]): void {
    this.customerSpaceDataSource.data = this.mapUsers(newData);
    this.onAftereRefreshData();
  }
}
